/*
 * The typing line, as every room that has one draws it: "Bea is typing …" over a message box,
 * from the signals the room's own module keeps.
 *
 * A module knows which box a signal belongs to and whether its room has a line at all — a direct
 * conversation and a thread in the chat module, an AI chat with more than one owner — and keeps its
 * signals as it likes. What the line *says* is the same decision everywhere, so it is made once here:
 *
 * - **who** — everybody whose last signal is younger than the window, never the reader (the server
 *   echoes one's own keystrokes back to the sender, and they are never news), and only people the
 *   reader's directory can name;
 * - **when it changes by itself** — a signal grows old with nothing else happening, so the line holds
 *   the clock as state and wakes when the oldest name it shows expires, rather than staying on screen
 *   until the next event happens to re-render it;
 * - **how it reads** — coalesced at two names: one, two, then "Several people". A list of five names
 *   under a box is a paragraph that moves, and nobody reads it.
 *
 * The element that shows the sentence is the module's: it must exist while its box does (a live
 * region has to be there before the change it announces, WCAG 4.1.3), and an empty sentence keeps the
 * line in place without words.
 */

import { useEffect, useMemo, useState } from "react"
import { formatList, LOCALE } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import { useModel } from "../store/store.ts"

/**
 * How long one signal stands for. A message box sends at most one signal per `typingIntervalMs`
 * (3 s, `core/composer/useComposerCore.ts`), so the window has to be longer than that or a line would
 * blink between two keystrokes of the same sentence; and short enough that somebody who walked away
 * stops "typing" while the reader is still looking.
 */
export const TYPING_TTL_MS = 5000

/** One room's signals: who, by user id, and when their last signal came (epoch ms). */
export type TypingSignals = Readonly<Record<number, number>>

/** A signal of somebody the reader can name. */
interface NamedSignal {
	name: string
	at: number
}

/**
 * The people a room's signals name now, in the reader's alphabetical order: everybody but the reader
 * whose last signal is younger than the window at `now`, and when the line will next change by itself
 * (0 when it will not).
 */
export function liveTypers(
	known: readonly NamedSignal[],
	now: number,
): { names: string[]; nextChange: number } {
	const live = known.filter((signal) => now - signal.at < TYPING_TTL_MS)
	const nextChange = live.length === 0 ? 0 : Math.min(...live.map((s) => s.at)) + TYPING_TTL_MS
	return {
		names: live.map((signal) => signal.name).sort((a, b) => a.localeCompare(b, LOCALE)),
		nextChange,
	}
}

/** What a set of names says, or "" while nobody is writing: one, two, or "Several people". */
export function typingSentence(names: readonly string[]): string {
	const first = names[0]
	const second = names[1]
	if (first === undefined) return ""
	if (second === undefined) return t("people.typing.single", { name: first })
	if (names.length === 2) return t("people.typing.pair", { names: formatList([first, second]) })
	return t("people.typing.several")
}

/**
 * The people a room's signals name now, by `liveTypers`' rule (never the reader, only people the
 * directory names, younger than the window), kept current as signals grow old. `signals` is the
 * room's own record, and undefined while it has none.
 */
export function useLiveTypers(signals: TypingSignals | undefined): string[] {
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)

	// The clock this reads is state and not `Date.now()` at render time: nothing else changes when a
	// signal grows old, so without it a line would stay on screen until the next event.
	const [now, setNow] = useState(() => Date.now())

	const known = useMemo(() => {
		const out: NamedSignal[] = []
		for (const [id, at] of Object.entries(signals ?? {})) {
			const userId = Number(id)
			const name = users[userId]?.displayName ?? ""
			if (userId !== meId && name !== "") out.push({ name, at })
		}
		return out
	}, [signals, users, meId])

	const { names, nextChange } = liveTypers(known, now)
	useEffect(() => {
		if (nextChange === 0) return
		const timer = setTimeout(() => setNow(Date.now()), Math.max(0, nextChange - Date.now()))
		return () => clearTimeout(timer)
	}, [nextChange])

	return names
}

/**
 * A room's typing line as it reads now: the sentence its signals make, kept current as signals grow
 * old. `signals` is the room's own record, and undefined while it has none.
 */
export function useTypingSentence(signals: TypingSignals | undefined): string {
	return typingSentence(useLiveTypers(signals))
}
